import { useState } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Search, ChevronLeft, ChevronRight, ArrowLeft, Printer } from 'lucide-react';
import { fetchShippingRunsheetDetail, type RunsheetDetailResponse } from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Stat } from '../ui/data.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd } from '../lib/format.js';

/**
 * S16b — one tour (N° feuille de route) and the packages inside it.
 * Route: /shipping/runsheets/:idRunsheet — list at /shipping/runsheets.
 */
export function RunsheetDetailPage(): React.ReactElement {
  const params = useParams<{ idRunsheet?: string }>();
  const idRunsheet = Number(params.idRunsheet);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const detailQuery = useQuery({
    queryKey: ['shipping', 'runsheets', idRunsheet, query, page],
    queryFn: () =>
      fetchShippingRunsheetDetail(idRunsheet, {
        search: query.trim() || undefined,
        page,
        limit: 50,
      }),
    enabled: Number.isInteger(idRunsheet) && idRunsheet > 0,
  });

  const data: RunsheetDetailResponse | undefined = detailQuery.data;
  const header = data?.header;
  const parcels = data?.parcels ?? [];
  const totalPages = data?.totalPages ?? 1;

  if (detailQuery.isLoading) {
    return (
      <div className="py-24 text-center">
        <Loading label="Chargement de la tournée…" />
      </div>
    );
  }

  if (detailQuery.isError || header === undefined) {
    return (
      <div className="space-y-6 animate-fade">
        <PageHeader
          index="S16"
          eyebrow="Gestion Logistique"
          title="Tournée introuvable"
          purpose="Ce N° de feuille de route n'existe pas (ou plus) dans les données synchronisées."
        />
        <Panel>
          <div className="p-6 text-center text-sm text-ink-soft">
            <p className="mb-4">Aucune tournée correspondant au numéro {params.idRunsheet ?? '—'}.</p>
            <Link to="/shipping/runsheets">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                Retour aux feuilles de route
              </Button>
            </Link>
          </div>
        </Panel>
      </div>
    );
  }

  const deliveredPct = header.packageCount > 0 ? Math.round((header.deliveredCount / header.packageCount) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade">
      <PageHeader
        index="S16"
        eyebrow="Gestion Logistique"
        title={`Feuille de route N° ${header.idRunsheet}`}
        purpose="Tournée du middleware Shiparon : les colis qui y sont embarqués, leur statut de livraison et leur montant COD."
        meta={[
          { label: 'Colis', value: header.packageCount.toLocaleString('fr-FR') },
          { label: 'COD', value: formatTnd(header.totalCod) },
          { label: 'Livrés', value: `${deliveredPct} %` },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/shipping/runsheets">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                Liste
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              title="Imprimer la décharge officielle"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" />
              Imprimer la décharge
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void detailQuery.refetch()}
              disabled={detailQuery.isFetching}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${detailQuery.isFetching ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        }
      />

      {/* Meta & Financial cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat
          label="Livreur"
          value={header.livreurNom ?? header.livreurRaw ?? '—'}
          {...(header.livreurTel ? { hint: header.livreurTel } : {})}
        />
        <Stat label="Matricule" value={header.matricule ?? '—'} />
        <Stat label="Agence" value={header.agence ?? '—'} />
        <Stat
          label="Statut"
          value={header.validated ? 'Validée (Clôturée)' : 'En cours (Non clôturée)'}
          tone={header.validated ? 'good' : 'alarm'}
        />
        <Stat
          label="Total COD"
          value={formatTnd(header.totalCod)}
          hint={`${header.deliveredCount} / ${header.packageCount} livrés`}
        />
        <Stat
          label="Frais Livraison"
          value={formatTnd(header.deliveryFee)}
          hint={`Tarif: ${header.unitFee.toFixed(3)} DT`}
        />
        <Stat
          label="Montant Net MaPara"
          value={formatTnd(header.netAmount)}
          tone="good"
          hint="Net à encaisser"
        />
      </div>

      {/* Parcels Table Panel */}
      <Panel>
        <div className="p-4 border-b border-border flex items-center justify-between gap-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setQuery(search);
            }}
            className="flex items-center gap-2 max-w-sm w-full"
          >
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-ink-faint" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher code barre, client, tél, ville…"
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-paper border border-border rounded-md text-ink placeholder:text-ink-faint focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <Button type="submit" size="sm" variant="outline">
              Filtrer
            </Button>
          </form>
          <div className="text-xs text-ink-soft">
            <strong>{data?.total ?? 0}</strong> colis dans cette tournée
          </div>
        </div>

        {parcels.length === 0 ? (
          <div className="py-12 text-center text-sm text-ink-soft">
            Aucun colis trouvé pour cette feuille de route.
          </div>
        ) : (
          <div className="p-3">
            <TableShell>
              <Table>
                <THead>
                  <Tr>
                    <Th>Code barre</Th>
                    <Th>Destinataire</Th>
                    <Th>Téléphone</Th>
                    <Th>Ville</Th>
                    <Th align="right">COD</Th>
                    <Th>État</Th>
                    <Th>Paiement</Th>
                  </Tr>
                </THead>
                <TBody>
                  {parcels.map((p) => (
                    <Tr key={p.codeBarre} className="hover:bg-cream/40 transition-colors">
                      <Td>
                        <span className="font-mono text-xs font-bold text-ink">{p.codeBarre}</span>
                        {p.facture && (
                          <div className="font-mono text-[10px] text-ink-faint">Fct: {p.facture}</div>
                        )}
                      </Td>
                      <Td>
                        <span className="text-xs font-medium text-ink">{p.clientName ?? '—'}</span>
                      </Td>
                      <Td>
                        <span className="font-mono text-xs text-ink-soft">{p.clientPhone ?? '—'}</span>
                      </Td>
                      <Td>
                        <span className="text-xs text-ink-soft">{p.location ?? '—'}</span>
                      </Td>
                      <Td align="right">
                        <span className="font-mono text-xs font-semibold text-ink">
                          {formatTnd(p.shiparonPrice)}
                        </span>
                      </Td>
                      <Td>
                        <span className="inline-flex items-center text-[10px] font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                          {p.shiparonEtatLabel}
                        </span>
                      </Td>
                      <Td>
                        {p.isPaid ? (
                          <span className="inline-flex items-center text-[10px] font-bold bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                            Payé
                          </span>
                        ) : (
                          <span className="text-xs text-ink-faint">—</span>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </TableShell>

            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-2 mt-2 border-t border-border text-xs text-ink-soft">
                <span>
                  Page <strong>{page}</strong> sur <strong>{totalPages}</strong>
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Suivant <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
